<template>
  <component
    :is="component"
    :to="to"
    :style="{
      'background-image': videoThumb ? `url('${videoThumb}')` : false,
      'width': width ? `${width}px` : null,
      'height': '100svh'
    }"
    :class="[
      $style['tile'],
      {[$style['tile--no-border-radius']]: noBorderRadius},
      {[$style['tile--no-border-radius-mobile']]: noBorderRadiusMobile},
      $style['tile--aspect-' + aspectRatio]
    ]">
    <picture
      v-if="pictures"
      ref="image"
      :class="$style['image']">
      <source
        v-for="(media, key) in pictures"
        :key="key"
        :srcset="media.src"
        :media="media.breakpoint">
      <img
        :class="$style['image']"
        data-test-id="home-hero"
        :src="pictures[0].src"
        :alt="alt"
        :style="{
          'width': width ? `${width}px` : null,
          'height': '100svh'
        }">
    </picture>
    <img
      v-else
      ref="image"
      data-test-id="home-hero"
      :class="$style['image']"
      :srcset="srcset"
      :width="width"
      :height="height"
      sizes="100vw"
      :alt="alt"
      :src="src">

    <div
      ref="content"
      :class="[
        $style['tile__content']
      ]">
      <slot />
    </div>
  </component>
</template>
<script setup>

const props = defineProps({
  to: {
    type: Object,
    default: null
  },
  src: {
    type: String,
    default: null
  },
  srcset: {
    type: String,
    default: null
  },
  alt: {
    type: [Boolean, String],
    default: null
  },
  noLazy: {
    type: Boolean,
    default: false
  },
  pictures: {
    type: Array,
    default: null
  },
  big: {
    type: Boolean,
    default: false
  },
  videoThumb: {
    type: String,
    default: null
  },
  width: {
    type: [String, Number],
    default: null
  },
  height: {
    type: [String, Number],
    default: null
  },
  aspectRatio: {
    type: String,
    default: null
  },
  noBorderRadius: {
    type: Boolean,
    default: false
  },
  noBorderRadiusMobile: {
    type: Boolean,
    default: false
  }
})

const nuxtLink = resolveComponent('nuxt-link')
const component = computed(() => {
  return props.to ? nuxtLink : 'div'
})

const content = ref()
const image = ref()

</script>
<style lang="scss" module>
/* stylelint-disable no-descending-specificity */
.tile {
  background-color: $white;
  display: flex;
  width: 100%;
  max-width: 100%;
  height: auto;
  position: relative;
  overflow: hidden;

  &--video {
    background-size: 100%;
    background-repeat: no-repeat;
    width: 100%;

    .smart-image {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
    }
  }

  &:focus:is([href]),
  &:hover:is([href]) {
    .image,
    .smart-image {
      transform: scale(1.02);
    }
  }

  &__content {
    align-items: flex-start;
    justify-content: flex-end;
    display: flex;
    flex-direction: column;
    padding: $spacing $spacing $spacing * 22;
    position: absolute;
    bottom: 0;
    left: 50%;
    height: 100%;
    width: 100%;
    max-width: 960px;
    transform: translateX(-50%);
    text-wrap: balance;
    z-index: 3;

    @media (max-width: #{$breakpoint-mobile}) {
      padding: $spacing * 2 $spacing * 2 $spacing * 8;
    }

    &--center {
      align-items: center;
    }
  }

  .image,
  .smart-image.smart-image {
    height: auto;
    width: 100%;
    object-fit: cover;
    transform: scale(1);
    transition: transform $animate-time $transition-easing;

  }

  &--picture {
    height: auto;
    object-fit: cover;
  }

  &--aspect-3-2 {
    &::before {
      border: 1px solid transparent;
      content: '';
      padding-top: 56.25%;
    }

    &>.image {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
    }
  }

  &--aspect-6-8 {
    &::before {
      border: 1px solid transparent;
      content: '';
      padding-top: 133.3%;
    }

    &>.image{
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &--aspect-16-9 {
    &::before {
      border: 1px solid transparent;
      content: '';
      padding-top: 56%;
    }

    &>.image {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: fill;
    }
  }

  &--aspect-4-5 {
    &::before {
      content: '';
      padding-top: 125%;
    }

    .image {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
    }
  }

  &--no-border-radius {
    border-radius: 0;
  }

  &--no-border-radius-mobile {
    @media (max-width: #{$breakpoint-mobile}) {
      border-radius: 0;
    }
  }
}
</style>
